import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  viewChild,
} from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';

export type IconButtonVariant = 'ghost' | 'secondary' | 'primary';
export type IconButtonSize = 'md' | 'lg' | 'field';

const SIZES: Record<IconButtonSize, string> = {
  md: 'size-10',
  lg: 'size-11',
  field: 'size-14',
};

const VARIANTS: Record<IconButtonVariant, string> = {
  ghost:
    'text-stone-600 hover:bg-stone-100 hover:text-ink dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-stone-50',
  secondary:
    'border border-stone-300 bg-white text-ink hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-50 dark:hover:bg-stone-800',
  primary: 'bg-bsite-400 text-ink hover:bg-bsite-300',
};

/** Pulsante con la sola icona: l'etichetta accessibile è obbligatoria. */
@Component({
  selector: 'bs-icon-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'inline-flex' },
  template: `
    <button
      #control
      [type]="type()"
      [class]="classes()"
      [disabled]="disabled()"
      [attr.aria-label]="label()"
      [attr.title]="label()"
      [attr.aria-expanded]="expanded()"
      [attr.aria-controls]="controls()"
      [attr.aria-haspopup]="hasPopup()"
      [attr.aria-pressed]="pressed()"
      [attr.data-testid]="testId()"
    >
      <bs-icon [name]="icon()" [size]="iconSize()" />
      @if (dot()) {
        <span
          class="absolute right-2.5 top-2.5 size-2 rounded-full border-2 border-white bg-bsite-400 dark:border-stone-950"
          aria-hidden="true"
        ></span>
      }
    </button>
  `,
})
export class IconButtonComponent {
  readonly icon = input.required<IconName>();
  readonly label = input.required<string>();
  readonly variant = input<IconButtonVariant>('ghost');
  readonly size = input<IconButtonSize>('md');
  readonly type = input<'button' | 'submit'>('button');
  readonly disabled = input(false);
  readonly expanded = input<boolean | null>(null);
  readonly controls = input<string | null>(null);
  readonly hasPopup = input<'dialog' | 'menu' | null>(null);
  readonly pressed = input<boolean | null>(null);
  /** Pallino giallo di segnalazione, per esempio sulle notifiche. */
  readonly dot = input(false);
  readonly testId = input<string | null>(null);

  private readonly control = viewChild.required<ElementRef<HTMLButtonElement>>('control');

  protected readonly iconSize = computed(() => (this.size() === 'field' ? 24 : 20));
  protected readonly classes = computed(
    () =>
      `relative inline-flex items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${SIZES[this.size()]} ${VARIANTS[this.variant()]}`,
  );

  focus(): void {
    this.control().nativeElement.focus();
  }

  get element(): HTMLButtonElement {
    return this.control().nativeElement;
  }
}
